import { z } from "zod";
export const MedioPagoEnum = z.enum([
    "EFECTIVO",
    "NEQUI",
    "BANCOLOMBIA",
    "TARJETA",
    "FIADO",
    "OTRO",
    "WOMPI",
]);
export const PagoDetalleEntradaSchema = z.object({
    medioPago: MedioPagoEnum,
    monto: z.number().positive("El monto a pagar debe ser positivo"),
    montoRecibido: z.number().positive().optional().nullable(),
    referencia: z.string().max(100).optional().nullable(),
    clienteId: z.string().uuid().optional().nullable(),
});
export const ItemACobrarSchema = z.object({
    pedidoItemId: z.string().uuid("ID de ítem inválido"),
    cantidad: z.number().int().positive("La cantidad a cobrar debe ser mayor a cero"),
});
// Precio especial SOLO para esta factura (ej. cliente leal) -- nunca toca
// Producto.precio (catálogo) ni PedidoItem.precioUnitario (la comanda
// original queda intacta). Independiente de itemsACobrar: aplica tanto en
// cobro total como parcial.
export const PrecioEditadoItemSchema = z.object({
    pedidoItemId: z.string().uuid("ID de ítem inválido"),
    precioUnitario: z.number().min(0, "El precio no puede ser negativo"),
});
export const CobrarMesaSchema = z.object({
    mesaId: z.string().uuid("ID de mesa inválido"),
    clienteId: z.string().uuid().optional().nullable(),
    clienteNombre: z.string().max(120).optional().nullable(),
    clienteDocumento: z.string().max(30).optional().nullable(),
    descuento: z.number().min(0).default(0),
    motivoDescuento: z.string().max(240).optional().nullable(),
    propina: z.number().min(0).default(0),
    // Sin min(1) a propósito: una mesa que pidió cuenta sin ningún producto
    // agregado tiene total $0 y no hay nada que pagar — CajaService exige al
    // menos un medio de pago solo cuando el total a cobrar es mayor a cero.
    pagos: z.array(PagoDetalleEntradaSchema),
    itemsACobrar: z.array(ItemACobrarSchema).optional().nullable(),
    preciosEditados: z.array(PrecioEditadoItemSchema).optional().nullable(),
    // Ver Factura.idempotencyKey: si la tablet reintenta este mismo cobro (modo
    // offline, PROGRESS.md sección 5g) porque no confirmó si la primera vez
    // llegó, manda la misma clave y el backend devuelve la factura ya creada
    // en vez de cobrar dos veces.
    idempotencyKey: z.string().max(64).optional().nullable(),
}).refine((data) => {
    if (data.descuento > 0 && !data.motivoDescuento) {
        return false;
    }
    return true;
}, {
    message: "El motivo del descuento es obligatorio cuando se aplica un descuento",
    path: ["motivoDescuento"],
});
// Revertir un cobro ya hecho por error (ej. se facturó la mesa equivocada).
// Solo aplica a cobros de mesa completos y recientes -- ver CajaService.
export const RevertirCobroSchema = z.object({
    motivo: z.string().min(3, "Cuéntanos brevemente por qué se revierte este cobro").max(300),
    sumarAMesaActual: z.boolean().optional().default(false),
});
export const ItemVentaRapidaSchema = z.object({
    productoId: z.string().uuid("ID de producto inválido"),
    cantidad: z.number().int().positive("La cantidad debe ser mayor a cero"),
    notas: z.string().max(240).optional().nullable(),
});
export const VentaRapidaSchema = z.object({
    items: z.array(ItemVentaRapidaSchema).min(1, "Debe agregar al menos un producto"),
    tipoPedido: z.enum(["PARA_LLEVAR", "SALON"]).default("PARA_LLEVAR"),
    clienteId: z.string().uuid().optional().nullable(),
    clienteNombre: z.string().max(120).optional().nullable(),
    clienteDocumento: z.string().max(30).optional().nullable(),
    descuento: z.number().min(0).default(0),
    motivoDescuento: z.string().max(240).optional().nullable(),
    propina: z.number().min(0).default(0),
    pagos: z.array(PagoDetalleEntradaSchema).min(1, "Debe ingresar al menos un medio de pago"),
    idempotencyKey: z.string().max(64).optional().nullable(),
}).refine((data) => {
    if (data.descuento > 0 && !data.motivoDescuento) {
        return false;
    }
    return true;
}, {
    message: "El motivo del descuento es obligatorio cuando se aplica un descuento",
    path: ["motivoDescuento"],
});
export const RegistrarGastoCajaSchema = z.object({
    monto: z.number().positive("El monto del gasto debe ser mayor a cero"),
    motivo: z.string().min(3, "Describe brevemente en qué se fue el dinero").max(240),
});
/** Corrige un gasto ya registrado — monto/motivo nuevos + por qué se corrige. */
export const EditarGastoCajaSchema = z.object({
    monto: z.number().positive("El monto del gasto debe ser mayor a cero"),
    motivo: z.string().min(2, "Describe brevemente en qué se fue el dinero").max(240),
    motivoModificacion: z.string().max(240).optional().default("Corrección de gasto"),
});
export const EliminarGastoCajaSchema = z.object({
    motivo: z.string().max(240).optional().default("Eliminación de gasto"),
});
// ------------------------------------------------------------------
// Respuestas (lo que el backend REALMENTE devuelve, para validar en
// runtime en el frontend — ver PROGRESS.md sección 5b/7). Los campos de
// dinero usan z.coerce.number(): Prisma serializa los `Decimal` como
// string en la mayoría de los endpoints, pero al menos uno
// (TurnosCajaService.obtenerResumenTurno) ya los convierte a number a
// mano — coerce acepta ambos sin arriesgar un falso rechazo.
// ------------------------------------------------------------------
const MontoCoercible = z.coerce.number();
export const PagoDetalleRespuestaSchema = z.object({
    id: z.string(),
    facturaId: z.string().optional(),
    clienteId: z.string().nullable().optional(),
    medioPago: MedioPagoEnum,
    monto: MontoCoercible,
    montoRecibido: MontoCoercible.nullable().optional(),
    cambio: MontoCoercible.nullable().optional(),
    referencia: z.string().nullable().optional(),
});
export const FacturaRespuestaSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    mesaId: z.string().nullable().optional(),
    mesaSesionId: z.string().nullable().optional(),
    cajeroId: z.string(),
    turnoCajaId: z.string().nullable().optional(),
    clienteNombre: z.string().nullable().optional(),
    clienteDocumento: z.string().nullable().optional(),
    subtotal: MontoCoercible,
    descuento: MontoCoercible,
    motivoDescuento: z.string().nullable().optional(),
    propina: MontoCoercible,
    total: MontoCoercible,
    creadoEn: z.string(),
    pagos: z.array(PagoDetalleRespuestaSchema),
    cajero: z.object({ id: z.string(), nombre: z.string() }).optional(),
    mesa: z.object({ id: z.string(), numero: z.number() }).nullable().optional(),
});
/** Entidad completa (prisma.turnoCaja + usuario): la devuelven abrir/cerrar/activo. */
export const TurnoCajaSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    usuarioId: z.string(),
    montoInicial: MontoCoercible,
    montoFinalEfectivo: MontoCoercible.nullable().optional(),
    diferenciaEfectivo: MontoCoercible.nullable().optional(),
    justificacionDiferencia: z.string().nullable().optional(),
    notas: z.string().nullable().optional(),
    estado: z.enum(["ABIERTO", "CERRADO"]),
    abiertoEn: z.string(),
    cerradoEn: z.string().nullable().optional(),
    usuario: z.object({ id: z.string(), nombre: z.string(), usuario: z.string(), rol: z.string() }),
});
/** Responde si HAY caja abierta en la sucursal (la haya abierto quien sea), no solo el usuario actual. */
export const TurnoActivoSucursalSchema = z.object({
    abierto: z.boolean(),
    turno: TurnoCajaSchema.nullable(),
});
export const GastoCajaRespuestaSchema = z.object({
    id: z.string(),
    turnoCajaId: z.string(),
    monto: MontoCoercible,
    motivo: z.string(),
    creadoEn: z.string(),
    usuario: z.object({ id: z.string(), nombre: z.string() }).optional(),
    consecutivo: z.number().optional().nullable(),
    turnoCaja: z
        .object({
        id: z.string(),
        cajero: z.string().optional().nullable(),
        abiertoEn: z.string().optional().nullable(),
        cerradoEn: z.string().optional().nullable(),
        estado: z.string().optional().nullable(),
    })
        .optional()
        .nullable(),
    // Rastro de corrección, si se editó (ver TurnosCajaService.editarGasto).
    montoOriginal: MontoCoercible.nullable().optional(),
    motivoModificacion: z.string().nullable().optional(),
    editadoPor: z.object({ id: z.string(), nombre: z.string() }).nullable().optional(),
    editadoEn: z.string().nullable().optional(),
});
/** Subconjunto de campos que arma a mano TurnosCajaService.obtenerResumenTurno (no es la entidad completa). */
export const ResumenTurnoSchema = z.object({
    turno: z.object({
        id: z.string(),
        usuario: z.object({ id: z.string(), nombre: z.string(), usuario: z.string().optional(), rol: z.string().optional() }),
        montoInicial: MontoCoercible,
        estado: z.string(),
        abiertoEn: z.string(),
        cerradoEn: z.string().nullable(),
        montoFinalEfectivo: MontoCoercible.nullable(),
        diferenciaEfectivo: MontoCoercible.nullable(),
        justificacionDiferencia: z.string().nullable(),
    }),
    totalFacturas: z.number(),
    totalVentasNetas: MontoCoercible,
    totalDescuentos: MontoCoercible,
    totalPropinas: MontoCoercible,
    ventasPorMedioPago: z.record(z.string(), MontoCoercible),
    totalGastos: MontoCoercible,
    gastos: z.array(GastoCajaRespuestaSchema),
    efectivoEsperadoEnCaja: MontoCoercible,
});
export const HistorialTurnoItemSchema = z.object({
    id: z.string(),
    cajeroId: z.string(),
    cajero: z.string(),
    rolCajero: z.string(),
    montoInicial: MontoCoercible,
    montoFinalEfectivo: MontoCoercible.nullable(),
    diferenciaEfectivo: MontoCoercible.nullable(),
    justificacionDiferencia: z.string().nullable(),
    estado: z.enum(["ABIERTO", "CERRADO"]),
    abiertoEn: z.string(),
    cerradoEn: z.string().nullable(),
    totalFacturas: z.number(),
    totalGastos: MontoCoercible,
    cantidadGastos: z.number(),
});
//# sourceMappingURL=caja.schema.js.map